import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LanguagePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onLanguageSelect: (lang: string) => void;
}

const LanguagePopup: React.FC<LanguagePopupProps> = ({ 
  isOpen, 
  onClose, 
  onLanguageSelect 
}) => {
  const { i18n } = useTranslation();
  
  const languages = [
    {
      code: 'id',
      name: 'Bahasa Indonesia',
      flag: '🇮🇩',
      description: 'Pilih Bahasa Indonesia'
    },
    {
      code: 'en',
      name: 'English',
      flag: '🇺🇸',
      description: 'Choose English'
    }
  ];

  const handleLanguageSelect = (langCode: string) => {
    i18n.changeLanguage(langCode);
    onLanguageSelect(langCode);
    localStorage.setItem('language', langCode);
    localStorage.setItem('language-selected', 'true');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Choose Language</h2>
                <p className="text-sm text-gray-600">Pilih Bahasa</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Language Options */}
          <div className="space-y-3">
            {languages.map((language) => (
              <motion.button
                key={language.code}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLanguageSelect(language.code)}
                className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                  i18n.language === language.code
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-800">
                      {language.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {language.description}
                    </p>
                  </div>
                  {i18n.language === language.code && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              You can change the language anytime from the menu • 
              Anda dapat mengubah bahasa kapan saja dari menu
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LanguagePopup;
